import { useState } from 'react'
import { juntar } from './ui'

/**
 * Foto do contato, com iniciais quando não houver.
 *
 * A maioria não tem foto — quem esconde por privacidade e quem nunca pôs uma.
 * Na primeira base real foram 248 de 924. Então o caso comum é o das
 * iniciais, e é ele que precisa ficar apresentável, não o contrário.
 *
 * A cor de fundo vem do próprio nome, sempre a mesma para a mesma pessoa:
 * dá para reconhecer a conversa de relance mesmo sem foto. São tons da marca,
 * não matizes aleatórios, para a lista não virar arco-íris.
 */

/** Tons de azul e cinza-azulado, todos escuros o bastante para texto branco. */
const TONS = ['#1f6feb', '#1a5fcc', '#2a78d6', '#3d5a80', '#4a5768', '#2f6f8f']

function corDoNome(nome: string): string {
  let soma = 0
  for (let i = 0; i < nome.length; i++) soma = (soma + nome.charCodeAt(i)) % 997
  return TONS[soma % TONS.length] as string
}

function iniciais(nome: string): string {
  const partes = nome
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0 && /\p{L}/u.test(p[0] as string))

  if (partes.length === 0) {
    // Nome que é só o número: os dois últimos dígitos identificam melhor que
    // os dois primeiros, que são o DDI de todo mundo.
    const digitos = nome.replace(/\D/g, '')
    return digitos.slice(-2) || '?'
  }
  if (partes.length === 1) return (partes[0] as string).slice(0, 2).toUpperCase()
  return ((partes[0] as string)[0] + (partes[partes.length - 1] as string)[0]).toUpperCase()
}

/**
 * Quem já respondeu "não tenho foto" nesta sessão.
 *
 * O cartão da conversa é montado de novo a cada rolagem da lista e a cada
 * atualização dela, e sem esta memória o navegador repetia o pedido para os
 * mesmos cem contatos. A resposta vazia já vem com cache de dez minutos, mas
 * guardar aqui evita até a ida ao cache e o piscar do quadrado cinza.
 */
const semFotoConhecidos = new Set<string>()

export function Avatar({
  contatoId,
  nome,
  tamanho = 'medio',
  cinza = false,
}: {
  contatoId: string
  nome: string
  tamanho?: 'pequeno' | 'medio'
  /**
   * Foto sem cor, para acompanhar o cartão que já foi lido.
   *
   * A foto era a única coisa colorida no cartão cinza e puxava o olho para a
   * conversa resolvida, competindo com as que ainda esperam resposta. Em
   * cinza ela continua reconhecível — o rosto e a forma são o que identifica,
   * não a cor — e a lista volta a destacar só o que tem pendência.
   *
   * Vale também para as iniciais: o fundo colorido delas acinzenta junto,
   * senão o contato sem foto ficaria mais chamativo que o contato com foto.
   */
  cinza?: boolean
}) {
  const [semFoto, setSemFoto] = useState(() => semFotoConhecidos.has(contatoId))

  const classe = juntar(
    'flex shrink-0 items-center justify-center overflow-hidden rounded-full font-medium text-white',
    tamanho === 'pequeno' ? 'h-8 w-8 text-[11px]' : 'h-10 w-10 text-xs',
    // A mesma duração do cartão: quando a conversa passa a lida, a cor sai da
    // foto junto com o verde do fundo, e não num segundo passo.
    'transition-[filter] duration-500',
    cinza && 'grayscale',
  )

  if (semFoto) {
    return (
      <span className={classe} style={{ backgroundColor: corDoNome(nome) }} aria-hidden="true">
        {iniciais(nome)}
      </span>
    )
  }

  return (
    <img
      src={`/api/contatos/${contatoId}/foto`}
      alt=""
      // 404 é o caminho normal, não erro: a rota responde assim quando o
      // contato não tem foto ou a Evolution está fora.
      onError={() => {
        semFotoConhecidos.add(contatoId)
        setSemFoto(true)
      }}
      className={juntar(classe, 'bg-tinta-100 object-cover')}
    />
  )
}
